import { useMemo, useState } from 'react';
import { miktarMetni } from '../bicim';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import { sayi } from '../ozetHesaplari';
import { zararinaSatis, type ZararSatiri } from '../uyarilar';
import type { DetayGovdeProps } from './detayTipleri';
import { AramaKutusu, aramaSuz, DetayMesaj, DetayYukleniyor } from './ortak';

/** SQL 141 sonrasi 990054 `stok_kodu` ile baslar; kolon yoksa satir urun detayina gecemez. */
interface ZararDetaySatiri extends ZararSatiri {
  stok_kodu?: string | null;
}

/**
 * M5a — zararina satis (spec §7.5). 990054 anahtari UyarilarBlok'unkiyle ayni -> sayfa onbelleginden, 0 ek
 * istek (SOZLESME SAPMASI 3). Satir sirasi ds'inki (zarar azalan).
 */
export function ZararDetay({ kaynaklar, donem, sube, kanal, ac }: DetayGovdeProps) {
  const [arama, setArama] = useState('');
  const q = useSaSorgu<ZararDetaySatiri>(kaynaklar.zararinaSatis, { donem, sube, kanal }, true, { onbellekten: true });
  const veri = q.data?.data;
  const gorunen = useMemo(
    () => aramaSuz(veri ?? [], arama, (r) => [String(r.urun), String(r.kategori), String(r.stok_kodu ?? '')]),
    [veri, arama],
  );

  let icerik;
  if (!kaynaklar.zararinaSatis)
    icerik = <DetayMesaj testid="zarar-detay-kaynak-yok">Zararına satış raporu bu firmada tanımlı değil.</DetayMesaj>;
  else if (q.isLoading) icerik = <DetayYukleniyor testid="zarar-detay-yukleniyor" />;
  else if (q.isError) icerik = <DetayMesaj testid="zarar-detay-hata">Zararına satışlar yüklenemedi.</DetayMesaj>;
  else if ((veri ?? []).length === 0)
    icerik = <DetayMesaj testid="zarar-detay-bos">Dönemde maliyetin altında satılan ürün yok.</DetayMesaj>;
  else {
    const toplam = zararinaSatis(veri ?? []);
    icerik = (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <AramaKutusu testid="zarar-detay-ara" placeholder="Ürün ya da kategori ara" ariaLabel="Ürün ara" value={arama} onChange={setArama} />
          {toplam && (
            <span data-testid="zarar-detay-toplam" className="text-[12px] text-ink-500 tnum">
              {fmtNum(toplam.adet)} ürün · toplam zarar {fmtTRY(toplam.toplam)}
            </span>
          )}
        </div>
        {gorunen.length === 0 ? (
          <DetayMesaj testid="zarar-detay-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="zarar-detay-tablo" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-left font-medium py-2 px-3">Kategori</th>
                  <th className="text-right font-medium py-2 px-3">Miktar</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Ort. satış fiyatı</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Birim maliyet</th>
                  <th className="text-right font-medium py-2 pl-3">Zarar</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((r) => {
                  const kod = r.stok_kodu ? String(r.stok_kodu) : null;
                  return (
                    <tr key={kod ?? String(r.urun)} data-testid={`zarar-detay-satir-${kod ?? String(r.urun)}`} className="border-b border-border last:border-b-0">
                      <td className="py-2 pr-3 max-w-0 w-full">
                        {kod ? (
                          <button
                            type="button"
                            data-testid={`zarar-detay-urun-${kod}`}
                            onClick={() => ac({ tur: 'urun', stokKodu: kod, urunAdi: String(r.urun) })}
                            className="focus-ring block w-full truncate text-left text-ink-900 hover:underline underline-offset-2"
                          >
                            {r.urun}
                          </button>
                        ) : (
                          <span className="block truncate text-ink-900">{r.urun}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-ink-500 whitespace-nowrap">{r.kategori}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-700">{miktarMetni(sayi(r.satilan_miktar))}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(sayi(r.ort_satis_fiyat))}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(sayi(r.birim_maliyet))}</td>
                      <td className="py-2 pl-3 text-right tnum text-neg-700 whitespace-nowrap">{fmtTRY(sayi(r.zarar))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return <div data-testid="zarar-detay">{icerik}</div>;
}
